import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import EditIcon from '@mui/icons-material/Edit'

import axios from '../../dataClient/dataClient.js'
import { billSchema } from '../../schemas/billSchema.js'
import { calculateBillTotal } from '../../util/strings.js'
import { formatBillFromServer } from '../../util/billFormatting.js'
import { FlexEndContainer, PaddedForm, StyledDialog, StyledDialogContent } from '../styles/Containers.js'
import { ErrorMessage } from '../common/UserMessage.js'
import Button, { SecondaryButton, SecondaryIconButton } from '../common/Button.js'
import BillForm from './BillForm.js'

function EditDraftBillButton({ bill, onlyApplicationFeeAllowed = false, editingBill, onSuccess = () => {}, handleUpdateEditingBill = () => {} }) {
  const [isVisible, setIsVisible] = useState(false)
  const [updatedBill, setUpdatedBill] = useState(bill)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  function handleClose() {
    setIsVisible(false)
    handleUpdateEditingBill()
    reset({ ...updatedBill })
    setError(false)
    setIsLoading(false)
  }

  const methods = useForm({ resolver: yupResolver(billSchema), defaultValues: updatedBill })
  const { handleSubmit, reset } = methods

  const onError = (errors, e) => console.error('Error', errors, e)

  async function onSubmit(data) {
    setIsLoading(true)
    setError(false)

    const total = calculateBillTotal({ lineItems: data.lineItems })
    const amountDue = (total * data.percentDue) / 100
    const { type, percentDue, lineItems } = data
    const body = { type, percentDue, lineItems, total, amountDue }

    axios
      .patch(`/bill/${bill.id}`, body)
      .then(({ data }) => {
        const formattedBill = formatBillFromServer(data.data)
        setUpdatedBill(formattedBill)
        setIsVisible(false)
        handleUpdateEditingBill()
        onSuccess(formattedBill)
      })
      .catch(e => {
        console.error('Failed to create the bill', e)
        setError(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const isMounted = useRef(true)

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    setUpdatedBill(bill)
  }, [bill])

  useEffect(() => {
    if (editingBill && editingBill.id === bill.id) {
      setUpdatedBill(editingBill)
      reset({ ...editingBill })
      setIsVisible(true)
    }
  }, [editingBill])

  return (
    <>
      <SecondaryIconButton onClick={() => handleUpdateEditingBill(bill.id)} aria-label={`Edit draft ${bill.secondaryId}`}>
        <EditIcon />
      </SecondaryIconButton>
      <StyledDialog maxwidth="1400px" open={isVisible} onClose={handleClose} keepMounted={false}>
        <StyledDialogContent>
          <FormProvider {...methods}>
            <PaddedForm onSubmit={handleSubmit(onSubmit, onError)}>
              <BillForm onlyApplicationFeeAllowed={onlyApplicationFeeAllowed} disabled={isLoading} />
              {error && <ErrorMessage>There was an error saving the draft. Please try again.</ErrorMessage>}
              <FlexEndContainer>
                <SecondaryButton onClick={handleClose} disabled={isLoading}>
                  Cancel
                </SecondaryButton>
                <Button type="submit" loading={isLoading}>
                  Save Draft
                </Button>
              </FlexEndContainer>
            </PaddedForm>
          </FormProvider>
        </StyledDialogContent>
      </StyledDialog>
    </>
  )
}

const billShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  secondaryId: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['APPLICATION_FEE', 'FEE_ESTIMATE', 'INVOICE']).isRequired,
  percentDue: PropTypes.number.isRequired,
  lineItems: PropTypes.array.isRequired
})

EditDraftBillButton.propTypes = {
  bill: billShape.isRequired,
  editingBill: billShape,
  onlyApplicationFeeAllowed: PropTypes.bool,
  onSuccess: PropTypes.func,
  handleUpdateEditingBill: PropTypes.func
}

export default EditDraftBillButton
